import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Trophy, Medal, Archive, FileEdit, ClipboardCheck, GitMerge,
  CalendarDays, ChevronRight, RefreshCw, LayoutGrid, Users,
  Swords, ListChecks, ShieldCheck, Activity, AlertCircle,
  CheckCircle2, Clock
} from "lucide-react";
import api from "@/lib/api";
import { Panel, StarDivider } from "@/components/common";
import { countPossibleDuplicatePlayers } from "@/lib/duplicateReview";

const STATE_NAMES = {
  draft: "Bozza",
  registration_open: "Iscrizioni aperte",
  registration_closed: "Iscrizioni chiuse",
  in_progress: "In corso",
  completed: "Completato",
  archived: "Archiviato",
  active: "Attivo",
};

function tournamentDate(tour) {
  const raw = tour.start_at ||
    (tour.start_date
      ? `${tour.start_date}T${tour.start_time || "00:00"}`
      : null);

  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(date) {
  return new Intl.DateTimeFormat("it-IT", {
    day: "2-digit", month: "long", year: "numeric",
    hour: "2-digit", minute: "2-digit"
  }).format(date);
}

function timeAgo(iso) {
  if (!iso) return "Data non disponibile";
  const diff = (Date.now() - new Date(iso).getTime()) / 60000;
  if (!Number.isFinite(diff)) return "Data non disponibile";
  if (diff < 1) return "Adesso";
  if (diff < 60) return `${Math.round(diff)} min fa`;
  if (diff < 1440) return `${Math.round(diff / 60)} ore fa`;
  return `${Math.round(diff / 1440)} g fa`;
}

function Metric({ icon: Icon, label, value, description, onClick }) {
  return (
    <button type="button" onClick={onClick} className="text-left w-full">
      <Panel className="p-5 h-full hover:border-[var(--gold)] transition-colors">
        <div className="flex items-start justify-between gap-2">
          <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
            {label}
          </span>
          <Icon className="w-5 h-5 text-[var(--gold)] shrink-0" />
        </div>
        <div className="heading text-4xl sm:text-5xl gold-text mt-3">
          {value}
        </div>
        <p className="font-rajdhani text-xs text-[var(--silver)] mt-2">
          {description}
        </p>
      </Panel>
    </button>
  );
}

function Task({ icon: Icon, title, count, description, onClick }) {
  const hasItems = count > 0;
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left flex items-center gap-4 p-4 rounded border border-[var(--steel)]/30 hover:border-[var(--gold)]/60 transition-colors bg-black/20"
    >
      <div className={`p-3 rounded ${hasItems ? "bg-amber-500/10" : "bg-emerald-500/10"}`}>
        <Icon className={`w-6 h-6 ${hasItems ? "text-amber-400" : "text-emerald-400"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="heading text-lg text-white">{title}</div>
        <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
          {description}
        </p>
      </div>
      <div className="text-right shrink-0">
        <div className={`heading text-3xl ${hasItems ? "text-amber-400" : "text-emerald-400"}`}>
          {count}
        </div>
        <ChevronRight className="w-4 h-4 text-[var(--silver)] ml-auto mt-1" />
      </div>
    </button>
  );
}

const QUICK_LINKS = [
  { label: "Tornei", description: "Gestione eventi", icon: Trophy, path: "/dashboard/tornei" },
  { label: "Campionati", description: "Stagioni e classifiche", icon: Medal, path: "/dashboard/campionati" },
  { label: "Lobby", description: "Assegnazioni e slot", icon: LayoutGrid, path: "/dashboard/lobby" },
  { label: "Risultati", description: "Verifica e approvazione", icon: ClipboardCheck, path: "/dashboard/risultati" },
  { label: "Classifiche Live", description: "Punteggi e MVP", icon: ListChecks, path: "/dashboard/classifiche" },
  { label: "Giocatori", description: "Anagrafiche e duplicati", icon: Users, path: "/dashboard/giocatori" },
  { label: "Verifiche K/D", description: "Controlli giocatori", icon: ShieldCheck, path: "/dashboard/verifiche-kd" },
  { label: "Squadre", description: "Iscrizioni e roster", icon: Swords, path: "/dashboard/squadre" },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const [
        tournamentsResponse,
        championshipsResponse,
        playersResponse,
        reviewResponse,
        dashboardResponse
      ] = await Promise.all([
        api.get("/tournaments", { params: { templates: false } }),
        api.get("/championships"),
        api.get("/players"),
        api.get("/players/duplicate-review"),
        api.get("/dashboard").catch(() => ({ data: {} }))
      ]);

      const tournaments = tournamentsResponse.data || [];
      const activeTournaments = tournaments.filter(
        (tour) => tour.state !== "archived"
      );

      const resultsResponses = await Promise.all(
        activeTournaments.map((tour) =>
          api.get("/results", {
            params: {
              tournament_id: tour.id,
              status: "PENDING_APPROVAL"
            }
          }).then(({ data: results }) => ({
            tournament: tour,
            results: results || []
          }))
        )
      );

      const pendingResults = resultsResponses.flatMap(
        ({ tournament, results }) =>
          results
            .filter((result) => result.status === "PENDING_APPROVAL")
            .map((result) => ({
              ...result,
              tournamentName: tournament.name
            }))
      );

      setData({
        tournaments,
        championships: championshipsResponse.data || [],
        pendingResults,
        duplicateCount: countPossibleDuplicatePlayers(
          playersResponse.data || [],
          reviewResponse.data?.excluded_player_ids || []
        ),
        activities: dashboardResponse.data?.activities || []
      });
    } catch (err) {
      console.error("Errore caricamento Home SAF:", err);
      setError("Impossibile caricare la panoramica. Riprova.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    if (!data) return null;

    const activeTournaments = data.tournaments.filter(
      (tour) => tour.state !== "archived"
    );
    const archivedTournaments = data.tournaments.filter(
      (tour) => tour.state === "archived"
    );
    const activeChampionships = data.championships.filter(
      (championship) => championship.state === "active"
    );
    const draftChampionships = data.championships.filter(
      (championship) => championship.state === "draft"
    );

    const now = Date.now();
    const upcoming = activeTournaments
      .map((tour) => ({ ...tour, eventDate: tournamentDate(tour) }))
      .filter((tour) => tour.eventDate && tour.eventDate.getTime() >= now)
      .sort((a, b) => a.eventDate - b.eventDate)
      .slice(0, 6);

    return {
      activeTournaments,
      archivedTournaments,
      activeChampionships,
      draftChampionships,
      upcoming
    };
  }, [data]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="heading text-4xl sm:text-5xl gold-text leading-tight pt-2">
            Centro di Controllo SAF
          </h1>
          <p className="font-rajdhani text-sm text-[var(--silver)] uppercase tracking-widest mt-1">
            Panoramica generale del gestionale
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="btn-outline-gold clip-beveled-sm px-5 py-2 flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
          Aggiorna
        </button>
      </div>

      {error && (
        <Panel className="p-5 flex items-center gap-3 text-red-400">
          <AlertCircle className="w-5 h-5" />
          {error}
        </Panel>
      )}

      {loading && !data && (
        <Panel className="p-10 text-center font-rajdhani text-[var(--silver)]">
          Caricamento panoramica...
        </Panel>
      )}

      {summary && (
        <>
          <section>
            <StarDivider label="Panoramica Generale" className="mb-4" />
            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
              <Metric
                icon={Trophy}
                label="Tornei da gestire"
                value={summary.activeTournaments.length}
                description="Tornei non archiviati"
                onClick={() => navigate("/dashboard/tornei")}
              />
              <Metric
                icon={Archive}
                label="Tornei archiviati"
                value={summary.archivedTournaments.length}
                description="Storico dei tornei"
                onClick={() => navigate("/dashboard/tornei")}
              />
              <Metric
                icon={Medal}
                label="Campionati attivi"
                value={summary.activeChampionships.length}
                description="Campionati in corso"
                onClick={() => navigate("/dashboard/campionati")}
              />
              <Metric
                icon={FileEdit}
                label="Campionati in bozza"
                value={summary.draftChampionships.length}
                description="Da configurare o avviare"
                onClick={() => navigate("/dashboard/campionati")}
              />
            </div>
          </section>

          <section>
            <Panel gold className="p-5 sm:p-6">
              <div className="flex items-center justify-between flex-wrap gap-2 mb-5">
                <h2 className="heading text-2xl gold-text flex items-center gap-2">
                  <AlertCircle className="w-6 h-6" />
                  Attività da completare
                </h2>
                <span className="font-rajdhani text-xs uppercase tracking-widest text-[var(--silver)]">
                  Controlli amministrativi
                </span>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <Task
                  icon={ClipboardCheck}
                  title="Risultati da approvare"
                  count={data.pendingResults.length}
                  description={
                    data.pendingResults.length
                      ? "Risultati inviati in attesa di approvazione"
                      : "Nessun risultato in attesa"
                  }
                  onClick={() => navigate("/dashboard/risultati")}
                />
                <Task
                  icon={GitMerge}
                  title="Possibili giocatori duplicati"
                  count={data.duplicateCount}
                  description={
                    data.duplicateCount
                      ? "Activision ID simili da verificare"
                      : "Nessun possibile duplicato da verificare"
                  }
                  onClick={() => navigate("/dashboard/giocatori")}
                />
              </div>

              {data.pendingResults.length > 0 && (
                <div className="mt-5">
                  <h3 className="font-rajdhani text-xs uppercase tracking-widest text-[var(--gold)] mb-3">
                    Ultimi risultati in sospeso
                  </h3>
                  <div className="space-y-2">
                    {data.pendingResults.slice(0, 5).map((result, index) => (
                      <button
                        key={`${result.match_id}-${result.team_id}-${index}`}
                        type="button"
                        onClick={() => navigate("/dashboard/risultati")}
                        className="w-full flex items-center justify-between gap-3 text-left p-3 border border-[var(--steel)]/30 hover:border-[var(--gold)]/60 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="font-rajdhani text-sm text-white truncate">
                            {result.team_name || "Squadra"}
                          </div>
                          <div className="font-rajdhani text-xs text-[var(--silver)] truncate">
                            {result.tournamentName} · Partita {result.match_number ?? "—"}
                          </div>
                        </div>
                        <ChevronRight className="w-4 h-4 text-[var(--gold)] shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Panel>
          </section>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <section className="xl:col-span-2">
              <Panel className="p-5 sm:p-6 h-full">
                <div className="flex items-center justify-between gap-3 mb-5">
                  <h2 className="heading text-2xl gold-text flex items-center gap-2">
                    <CalendarDays className="w-6 h-6" />
                    Prossimi Tornei
                  </h2>
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/tornei")}
                    className="font-rajdhani text-xs uppercase text-[var(--gold)] hover:underline"
                  >
                    Vedi tutti
                  </button>
                </div>

                {summary.upcoming.length === 0 ? (
                  <div className="py-10 text-center">
                    <CalendarDays className="w-9 h-9 mx-auto text-[var(--silver)] mb-3" />
                    <p className="font-rajdhani text-[var(--silver)]">
                      Nessun torneo futuro programmato
                    </p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {summary.upcoming.map((tour) => (
                      <button
                        type="button"
                        key={tour.id}
                        onClick={() => navigate("/dashboard/tornei")}
                        className="w-full flex items-center gap-4 p-4 border border-[var(--steel)]/30 hover:border-[var(--gold)]/60 transition-colors text-left"
                      >
                        <div className="shrink-0 text-center min-w-[72px]">
                          <div className="heading text-2xl text-[var(--gold)]">
                            {tour.eventDate.toLocaleDateString("it-IT", { day: "2-digit" })}
                          </div>
                          <div className="font-rajdhani text-xs uppercase text-[var(--silver)]">
                            {tour.eventDate.toLocaleDateString("it-IT", { month: "short" })}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="heading text-lg text-white truncate">
                            {tour.name}
                          </div>
                          <div className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                            {formatDate(tour.eventDate)}
                          </div>
                          <div className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                            {tour.type === "INTERNAL_SEALS" ? "Interno Seals" : "SAF"}
                            {" · "}
                            {STATE_NAMES[tour.state] || tour.state}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-[var(--gold)] shrink-0" />
                      </button>
                    ))}
                  </div>
                )}
              </Panel>
            </section>

            <section>
              <Panel className="p-5 sm:p-6 h-full">
                <h2 className="heading text-2xl gold-text flex items-center gap-2 mb-5">
                  <Activity className="w-6 h-6" />
                  Attività Recenti
                </h2>
                {data.activities.length === 0 ? (
                  <div className="py-10 text-center">
                    <CheckCircle2 className="w-9 h-9 mx-auto text-[var(--silver)] mb-3" />
                    <p className="font-rajdhani text-[var(--silver)]">
                      Nessuna attività recente disponibile
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {data.activities.slice(0, 6).map((activity, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 border-b border-[var(--steel)]/20 pb-3"
                      >
                        <Clock className="w-4 h-4 text-[var(--gold)] shrink-0 mt-1" />
                        <div>
                          <p className="font-rajdhani text-sm text-white">
                            {activity.action}
                          </p>
                          <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                            {timeAgo(activity.timestamp)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Panel>
            </section>
          </div>

          <section>
            <StarDivider label="Accessi Rapidi" className="mb-4" />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {QUICK_LINKS.map(({ label, description, icon: Icon, path }) => (
                <button
                  type="button"
                  key={path}
                  onClick={() => navigate(path)}
                  className="text-left"
                >
                  <Panel className="p-5 h-full hover:border-[var(--gold)] transition-colors">
                    <Icon className="w-7 h-7 text-[var(--gold)] mb-3" strokeWidth={1.5} />
                    <div className="heading text-lg text-white">{label}</div>
                    <p className="font-rajdhani text-xs text-[var(--silver)] mt-1">
                      {description}
                    </p>
                  </Panel>
                </button>
              ))}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
